import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { Callback } from '../../../modules/callback'
import { IFlowSubscriber } from '../../../modules/epflow'
import { Locale } from '../../../modules/locale'
import MainModule from '../../../modules/main'
import { escapeHtml, unescapeHtml } from '../../../modules/utils'

Locale.getInstance().registerKeySet('fr', 'create-event-add-image', {
  title: 'Ajoutez une image de couverture',
  description:
    'Ajoutez un visuel de couverture qui représente votre exposition. Assurez-vous d’en détenir les droits de diffusion. Le texte alternatif permet de donner une courte description de votre illustration, lorsque l’image est inaccessible aux utilisateurs.',
  festivalDescription:
    "Ajoutez un visuel de couverture qui représente votre festival. Assurez-vous d'en détenir les droits de diffusion. Le texte alternatif permet de donner une courte description de votre illustration, lorsque l'image est inaccessible aux utilisateurs.",
  placeholder: 'Écrivez ici...',
  altTextFr: 'Texte alternatif - Français*',
  altTextEn: 'Texte alternatif - Anglais*',
  imageCredit: 'Crédits photographiques',
  royaltyCheckbox: 'Cette image est libre de droits',
  errors: {
    image: 'Veuillez sélectionner une image',
    imageCredit: "Veuillez entrer le crédit de l'image",
    altTextFr: 'Veuillez entrer le texte alternatif en français',
    altTextEn: 'Veuillez entrer le texte alternatif en anglais',
    generic: 'Veuillez corriger les erreurs ci-dessous',
  },
})

Locale.getInstance().registerKeySet('en', 'create-event-add-image', {
  title: 'Add a cover image',
  description:
    'Add a cover visual that represents your exhibition. Make sure you have the copyrights needed to use it. Alternate text allows you to give a short description of your illustration, when the image is inaccessible to users.',
  festivalDescription:
    'Add a cover visual that represents your festival. Make sure you have the copyrights needed to use it. Alternate text allows you to give a short description of your illustration, when the image is inaccessible to users.',
  placeholder: 'Write here...',
  altTextFr: 'Alternative text - French*',
  altTextEn: 'Alternative text - English*',
  imageCredit: 'Image credit',
  royaltyCheckbox: 'This image is royalty free',
  errors: {
    image: 'Please select an image',
    imageCredit: 'Please enter the image credit',
    altTextFr: 'Please enter the alternative text in French',
    altTextEn: 'Please enter the alternative text in English',
    generic: 'Please correct the errors below',
  },
})

export interface EventImageData {
  image?: File
  imageUrl: string
  royaltyCheckbox: boolean
  imageCredit: string
  altTextFr: string
  altTextEn: string
}

export interface LvcCreateEventAddImage {
  flowGroup: string
  flowStep: string
  errors: any
  image: File
  royaltyCheckbox: boolean
  imageCredit: string
  altTextFr: string
  altTextEn: string
}

export class LvcCreateEventAddImage extends MinzeElement implements MinzeElement, IFlowSubscriber {
  showUserForm: boolean = true
  changeCheckboxCb = Callback.attach(this.updateCheckbox.bind(this))
  changeInputCb = Callback.attach(this.onInputChange.bind(this))
  attrs: Attrs = [
    ['flow-step', ''],
    ['data-type', 'event'],
    ['flow-group', ''],
  ]
  reactive: Reactive = [
    ['image', ''],
    ['royaltyCheckbox', false],
    ['imageCredit', ''],
    ['altTextFr', ''],
    ['altTextEn', ''],
    ['eventImageData', {}, false],
    ['imageUrl', '', false],
    [
      'errors',
      {
        image: false,
        imageCredit: false,
        altTextFr: false,
        altTextEn: false,
        generic: false,
      },
      false,
    ],
  ]

  async onReady(): Promise<void> {
    const mainModule = MainModule.getInstance()
    mainModule?.getFlowManager()?.subscribe(this)
  }

  onDestroy() {
    MainModule?.getInstance()?.getFlowManager()?.unsubscribe(this)
  }

  handleImageChange(event: any) {
    this.image = event.detail
    this.onInputChange()
  }

  public async updateCheckbox() {
    let royaltyCheckboxElement = this.select('[name="royaltyCheckbox"]')

    if (!royaltyCheckboxElement) {
      return
    }
    let royaltyCheckboxString = royaltyCheckboxElement?.getAttribute('checked') || ''
    this.royaltyCheckbox = royaltyCheckboxString === 'true'
    this.onInputChange()
  }

  onInputChange() {
    // We save the value of the inputs otherwise if we rerender the value will be lost
    const altTextFrElement: any = this.select('[name="altTextFr"]')
    const altTextEnElement: any = this.select('[name="altTextEn"]')
    const imageCreditElement: any = this.select('[name="imageCredit"]')

    this.altTextFr = altTextFrElement?.value
    this.altTextEn = altTextEnElement?.value
    this.imageCredit = imageCreditElement?.value

    this.eventImageData = {
      image: this.image,
      royaltyCheckbox: this.royaltyCheckbox,
      imageCredit: this.imageCredit,
      altTextFr: this.altTextFr,
      altTextEn: this.altTextEn,
    }

    // Get the FlowCore instance to save the data
    const flowCore = MainModule.getInstance().getFlowManager().getCore(this.flowGroup)

    if (flowCore) {
      flowCore.save()
    } else {
      console.warn('FlowCore instance not found, Saving aborted.')
    }
  }

  public async serialize(): Promise<unknown> {
    if (this.image) {
      this.imageUrl = await MainModule.getInstance().getAuth().uploadImage(this.image)
    }

    let eventImageData = {
      imageUrl: this.imageUrl,
      royaltyCheckbox: this.royaltyCheckbox,
      imageCredit: this.imageCredit,
      altTextFr: this.altTextFr,
      altTextEn: this.altTextEn,
    }
    return eventImageData
  }

  public async deserialize(data?: unknown): Promise<void> {
    // We get the data from the flowCore and we set the values of the inputs
    const d = (data as EventImageData) ?? {}
    //this.image = d.image;
    this.imageUrl = await MainModule.getInstance().getAuth().getImageUrl(d.imageUrl)
    this.royaltyCheckbox = d.royaltyCheckbox
    this.imageCredit = d.imageCredit
    this.altTextFr = d.altTextFr
    this.altTextEn = d.altTextEn

    this.eventImageData = {
      image: this.image,
      imageUrl: this.imageUrl,
      royaltyCheckbox: this.royaltyCheckbox,
      imageCredit: this.imageCredit,
      altTextFr: this.altTextFr,
      altTextEn: this.altTextEn,
    }
    this.rerender()
  }

  getImageSrc() {
    if (!this.image) {
      return this.imageUrl ?? ''
    } else {
      return URL.createObjectURL(this.image)
    }
  }

  public async validate(): Promise<boolean> {
    const altTextFrElement: any = this.select('[name="altTextFr"]')
    const altTextEnElement: any = this.select('[name="altTextEn"]')
    const imageCreditElement: any = this.select('[name="imageCredit"]')

    const altTextFr = unescapeHtml(altTextFrElement?.value)
    const altTextEn = unescapeHtml(altTextEnElement?.value)
    const imageCredit = unescapeHtml(imageCreditElement?.value)

    const image = this.image

    for (const key in this.errors) {
      this.errors[key] = false
    }

    if (!altTextFr) {
      this.errors.altTextFr = Locale.getKey('create-event-add-image.errors.altTextFr')
    }

    if (!altTextEn) {
      this.errors.altTextEn = Locale.getKey('create-event-add-image.errors.altTextEn')
    }

    if (!imageCredit && !this.royaltyCheckbox) {
      this.errors.imageCredit = Locale.getKey('create-event-add-image.errors.imageCredit')
    }

    if (!image && !this.imageUrl) {
      this.errors.image = Locale.getKey('create-event-add-image.errors.image')
    }

    let hasErrors = false
    for (const key in this.errors) {
      if (this.errors[key]) {
        hasErrors = true
      }
    }

    // if we have errors, go to the top of the page
    // and show generic error message
    if (hasErrors) {
      window.scrollTo(0, 0)
      this.errors.generic = Locale.getKey('create-event-add-image.errors.generic')
    }

    return !hasErrors
  }

  html = () => /*html*/ `<div class="container">

    <lvc-box mt="24px" textAlign="left">
      <lvc-text font-preset="titleMobileSmall">
        ${Locale.getKey('create-event-add-image.title')}
      </lvc-text>
    </lvc-box>

    <lvc-box mt="24px" textAlign="left" color="#1E007D" font-preset="title">
      ${Locale.getKey(
        `create-event-add-image.${
          this.dataType === 'festival' ? 'festivalDescription' : 'description'
        }`
      )}
    </lvc-box>

    <lvc-box mt="12px">
      <lvc-error error="${this.errors.generic}"></lvc-error>
    </lvc-box>

    <lvc-box mt="24px" textAlign="left" color="#1E007D" font-preset="title">
      <lvc-image-input-field default-value="${this.getImageSrc()}"></lvc-image-input-field>
    </lvc-box>

    ${
      this.errors.image
        ? `<lvc-box mt="12px">
      <lvc-error error="${this.errors.image}"></lvc-error>
    </lvc-box>`
        : ``
    }


    <lvc-box mt="24px">
      <lvc-checkbox default-value="${this.royaltyCheckbox}" on-change="${this.changeCheckboxCb}" label="${Locale.getKey('create-event-add-image.royaltyCheckbox')}" name="royaltyCheckbox"></lvc-checkbox>
    </lvc-box>

    <lvc-box class="form" mb="24px">
      <lvc-box mt="8px" textAlign="${window.matchMedia(' (max-width: 730px)').matches ? 'left' : 'center'}"></lvc-box>
        <lvc-box mt="24px">
          <lvc-input secondary-style="true" on-change="${this.changeInputCb}" default-value="${escapeHtml(this.imageCredit)}" border="1px solid #1E007D" id="image-credit" placeholder="${Locale.getKey('create-event-add-image.placeholder')}" name="imageCredit" error="${this.errors.imageCredit}" label="${Locale.getKey('create-event-add-image.imageCredit') + (this.royaltyCheckbox ? '' : '*')}"></lvc-input>
        </lvc-box>
        <lvc-box mt="24px">
          <lvc-input secondary-style="true" on-change="${this.changeInputCb}" default-value="${escapeHtml(this.altTextFr)}" border="1px solid #1E007D" id="alternative-text-fr" placeholder="${Locale.getKey('create-event-add-image.placeholder')}" name="altTextFr" error="${this.errors.altTextFr}" label="${Locale.getKey('create-event-add-image.altTextFr')}"></lvc-input>
        </lvc-box>
        <lvc-box mt="24px">
          <lvc-input secondary-style="true" on-change="${this.changeInputCb}" default-value="${escapeHtml(this.altTextEn)}" border="1px solid #1E007D" id="alternative-text-en" placeholder="${Locale.getKey('create-event-add-image.placeholder')}" name="altTextEn" error="${this.errors.altTextEn}" label="${Locale.getKey('create-event-add-image.altTextEn')}" ></lvc-input>
        </lvc-box>
      </lvc-box>
    </lvc-box>
  </div>
    `

  eventListeners: EventListeners = [
    ['lvc-image-input-field', 'lvc-image-input-field:change', this.handleImageChange.bind(this)],
  ]
}
